import CONFIG from "./config";
import log2 from "../js/log2-lite";

const log = log2("utils");
const isHighTrafficHostname = () => {
    const staticSites = ["www.vatanbilgisayar.com"];
    if (staticSites.includes(window.location.hostname)) {
        return true;
    } else if (window.location.hostname.includes("betnano")) {
        return true;
    }
    return false;
};

// CDN URL'i belirle
const getCdnUrl = () => {
    log.debug("getCdnUrl in static config", CONFIG);
    return CONFIG.IS_PROD_ENV ? "https://apicdn.supsis.live/api/" : CONFIG.TABLET_URL;
};

// CDN'den servis edilecek path'ler
const CDN_PATHS = [
    "public/settings/get-all",
    "sites/search/",
    "public/loginForms/get-all",
    "public/subjects/get-all",
    "public/web-analytics/rules",
    "public/web-analytics/page-status",
];

// Path'in CDN'den servis edilip edilmeyeceğini kontrol eder
const shouldUseCdn = (url) => {
    return CDN_PATHS.some((path) => url.startsWith(path) || url.includes(path));
};

// URL'i CDN path kontrolüne göre oluşturur
export const buildUrl = (path) => {
    const baseUrl = !CONFIG.IS_STAGE_ENV && shouldUseCdn(path) ? getCdnUrl() : CONFIG.TABLET_URL;
    return baseUrl + path;
};

const tryRequest = async (requestFn, maxAttempts = 10, retryDelay = 5000) => {
    const attempt = async (attempts = 1) => {
        try {
            if (attempts > 1) {
                log(`Deneme ${attempts}/${maxAttempts} - ${retryDelay / 1000} saniye bekleniyor...`);
                await new Promise((resolve) => setTimeout(resolve, retryDelay));
            }
            const response = await requestFn();
            if (response.status === 200) {
                return await response.json();
            }
            if (attempts < maxAttempts) {
                return attempt(attempts + 1);
            } else {
                return undefined;
            }
        } catch (ex) {
            if (attempts < maxAttempts) {
                return attempt(attempts + 1);
            } else {
                log(`${maxAttempts} deneme sonrası başarısız oldu`);
                return undefined;
            }
        }
    };

    return attempt();
};

export const fetchSiteData = () => {
    log("fetching site data");
    const cachedData = localStorage.getItem("supsis-site-data");
    if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);
        if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
            // 1 gün
            log("using cached site data");
            return Promise.resolve(data);
        }
    }

    const makeRequest = () => fetch(buildUrl(`sites/search/customer/${CONFIG.DOMAIN}`));

    return tryRequest(makeRequest).then((json) => {
        if (json) {
            log("site data", json);
            localStorage.setItem(
                "supsis-site-data",
                JSON.stringify({
                    data: json,
                    timestamp: Date.now(),
                })
            );
        }
        return json;
    });
};

// ek web chat kanalinda ayarlar kanala gore gelir; onbellek de kanal bazli tutulur
const CHANNEL_QUERY = CONFIG.CHANNEL_ID ? `&channel=${CONFIG.CHANNEL_ID}` : "";
const SETTINGS_CACHE_KEY = CONFIG.CHANNEL_ID ? `supsis-settings-${CONFIG.CHANNEL_ID}` : "supsis-settings";

export const fetchSiteSetting = (siteId) => {
    log("fetching site settings");
    if (isHighTrafficHostname()) {
        const cachedSettings = localStorage.getItem(SETTINGS_CACHE_KEY);
        if (cachedSettings) {
            const { data, timestamp } = JSON.parse(cachedSettings);
            if (Date.now() - timestamp < 60 * 1000) {
                // 1 saat
                log("using cached site settings");
                return Promise.resolve(data);
            }
        }
    }

    const makeRequest = () =>
        fetch(buildUrl(`public/settings/get-all?contain=visibilityConfig,liveTraffic,liveAnalytics,webAnalytics&lite=true${CHANNEL_QUERY}`), {
            headers: {
                "site-id": siteId,
            },
        });

    return tryRequest(makeRequest).then((settings) => {
        if (settings) {
            log("site settings fetched", settings);
            if (isHighTrafficHostname()) {
                localStorage.setItem(
                    SETTINGS_CACHE_KEY,
                    JSON.stringify({
                        data: settings,
                        timestamp: Date.now(),
                    })
                );
            }
        }
        return settings;
    });
};

// Chat acikken uygulanan gecici viewport kilidi icin orijinal content burada saklanir.
const VIEWPORT_BACKUP_ATTR = "data-supsis-viewport-backup";
const VIEWPORT_LOCK_VALUE = "maximum-scale=1.0";
let isViewPortWatched = false;

const getViewPortMeta = () => document.querySelector("meta[name='viewport']");

const buildLockedViewPort = (original) => original + ", " + VIEWPORT_LOCK_VALUE;

/**
 * Chat acikken iOS Safari'nin input focus'unda sayfayi otomatik yakinlastirmasini engeller.
 * user-scalable=no kullanilmaz: iOS 10+ tarafindan yok sayiliyor ve erisilebilirlik
 * denetimlerinde (WCAG 1.4.4 / Lighthouse) ihlal olarak isaretleniyor.
 */
const lockViewPort = function () {
    const meta = getViewPortMeta();
    if (!meta || meta.hasAttribute(VIEWPORT_BACKUP_ATTR)) return;
    const original = meta.getAttribute("content");
    // Viewport tanimi olmayan site responsive degildir; tanim eklemek layout'unu bozabilir.
    if (!original) return;
    // Musteri kendi maximum-scale degerini belirlemisse ona dokunmuyoruz.
    if (original.indexOf("maximum-scale") !== -1) return;
    meta.setAttribute(VIEWPORT_BACKUP_ATTR, original);
    meta.setAttribute("content", buildLockedViewPort(original));
    log("viewport locked");
};

/** Chat kapaninca musterinin orijinal viewport content'ini geri yazar. */
const unlockViewPort = function () {
    const meta = getViewPortMeta();
    if (!meta || !meta.hasAttribute(VIEWPORT_BACKUP_ATTR)) return;
    const original = meta.getAttribute(VIEWPORT_BACKUP_ATTR);
    meta.removeAttribute(VIEWPORT_BACKUP_ATTR);
    // Kilit acikken musterinin kendi kodu content'i degistirdiyse ustune yazmiyoruz.
    if (meta.getAttribute("content") !== buildLockedViewPort(original)) return;
    meta.setAttribute("content", original);
    log("viewport unlocked");
};

/**
 * ls-opened class'i chat'in acik/kapali durumunun tek kaynagi oldugu icin
 * body class degisimini izleyip viewport kilidini senkron tutar.
 * Viewport islemleri hicbir kosulda widget baslatma akisini kesmemeli.
 */
export const watchViewPort = function () {
    log("watchViewPort");
    try {
        if (isViewPortWatched || !window.MutationObserver || !document.body) return;
        let isLocked = false;
        const sync = () => {
            try {
                const shouldLock = document.body.classList.contains("ls-opened");
                if (shouldLock === isLocked) return;
                isLocked = shouldLock;
                if (shouldLock) lockViewPort();
                else unlockViewPort();
            } catch (e) {
                log("viewport sync failed", e);
            }
        };
        new MutationObserver(sync).observe(document.body, { attributes: true, attributeFilter: ["class"] });
        isViewPortWatched = true;
        sync();
    } catch (e) {
        log("watchViewPort failed", e);
    }
};

export const onDocReady = () => {
    return new Promise((resolve, reject) => {
        if (document.readyState === "complete" || document.readyState === "interactive") {
            setTimeout(resolve, 1);
        } else {
            document.addEventListener("DOMContentLoaded", resolve);
        }
    });
};



//////////////////
// WEBPACK FOOTER
// ./static/src/utils.js
// module id = q8yT
// module chunks = 1 2